import { Box, Button } from '@mui/material'

import { LayoutComposer, ListFooter, NoEntries, NoResults } from '@/components/List'
import { ListLoading } from '@/components/ListLoading'
import { StructuredVirtualList } from '@/components/StructuredVirtualList'
import { ComponentSize, FormColumn, ListOverlayEditCover } from '@/components/layout'
import { ReadOnlyTooltip } from '@/components/smartUtils/plan/ReadOnlyTooltip'

import { useAutoScroll } from '@/atoms'

import { useDataForList } from './hooks/useDataForList'
import { useListHandlers } from './hooks/useListHandlers'
import { useTexts } from './hooks/useTexts'

import { Header } from './Header'
import { Item } from './Item'

interface Props {
  places: Record<string, uui.domain.client.gps.wwgps.Place>
  selection: workwave.DeepReadonly<string[]>
  editing: boolean
}

const getItemHeight = () => 58

export function List(props: Props) {
  const { places, selection, editing } = props

  const { handleOnSelect, handleOnSelectAll, handleOnDoubleClick, handleOnClick } =
    useListHandlers(selection)
  const { structure, lastSelectedPlace, isLoading } = useDataForList()
  const texts = useTexts()

  useAutoScroll('places', lastSelectedPlace)

  const allSelected = selection.length >= structure.shownItems
  const multiSelect = selection.length > 1

  return (
    <FormColumn width={320} testid="form__column-primary">
      <Header />

      <Box width="100%" height="100%" flex="0 1 auto" position="relative">
        <LayoutComposer
          totalItems={structure.totalItems}
          shownItems={structure.shownItems}
          noResults={
            <NoResults title={texts.noResults.title} description={texts.noResults.description} />
          }
          noEntries={
            <NoEntries
              title={texts.noEntries.title}
              description={texts.noEntries.description}
              action={
                <ReadOnlyTooltip
                  render={preventEditing => (
                    <Button
                      size="large"
                      variant="outlined"
                      onClick={handleOnClick}
                      disabled={preventEditing || editing}
                      data-testid="no-entries-add-item"
                      data-trackid="no-entries-add-item"
                    >
                      {texts.noEntries.action}
                    </Button>
                  )}
                />
              }
            />
          }
          list={
            <ComponentSize
              render={size => {
                return isLoading ? (
                  <ListLoading />
                ) : (
                  // TODO: reset the scroll when the list options change
                  <>
                    <StructuredVirtualList
                      itemsById={places}
                      structure={structure}
                      listApiCategory="places"
                      getItemHeights={getItemHeight}
                      RenderItem={Item}
                      listHeight={size.height}
                      selectedItemIds={selection}
                      onSelect={handleOnSelect}
                      onDoubleClick={handleOnDoubleClick}
                      buffer={50}
                    />
                    {editing && <ListOverlayEditCover />}
                  </>
                )
              }}
            />
          }
        />
      </Box>

      {multiSelect && (
        <ListFooter
          label={texts.selectBtn(allSelected)}
          onToggleSelection={handleOnSelectAll}
          checked={allSelected}
        />
      )}
    </FormColumn>
  )
}
